import { Stack } from "@mui/material";
import React from "react";
import { IXProductDetailsProps } from "../../../../utils/interfaces/containers/XProductDetails.interfaces";
import { DEFAULT_FONT_FAMILY } from "../../../../utils/constants/common/fonts";
import {
  MAIN_COLOR,
  SECONDARY_COLOR,
} from "../../../../utils/constants/common/utils";
import { generateBorder } from "../../../../lib/generateBorder";
import { generateRadius } from "../../../../lib/generateRadius";

const DetailsWrapper: React.FC<
  IXProductDetailsProps & {
    children?: React.ReactNode[] | React.ReactNode;
  }
> = ({
  children,
  detailsGap = "10px",
  textColor = "#000000",
  textFontSize = "16px",
  // DETAILS TITLE
  titleColor = "#000000",
  titleFontSize = "10px",
  titleFontWeight = 600,
  titleLetterSpacing = "1px",

  // DETAILS ARTICLE NAME
  nameColor = "#000000",
  nameFontSize = "28px",
  nameFontFamily = DEFAULT_FONT_FAMILY,
  nameFontStyle = "normal",
  nameFontWeight = 600,

  // DETAILS ARTICLE PRICE
  priceColor = "#000000",
  priceFontSize = "28px",
  priceFontFamily = DEFAULT_FONT_FAMILY,
  priceFontStyle = "normal",
  priceFontWeight = 300,

  // DETAILS ARTICLE DISCOUNT
  discountColor = "#777777",
  discountFontSize = "24px",
  discountFontFamily = DEFAULT_FONT_FAMILY,
  discountFontStyle = "normal",
  discountFontWeight = 600,
  discountTextDecoration = "line-through",

  starIconColor = SECONDARY_COLOR,
  starIconSize = "20px",
  // COLOR_PICKER
  // colorPickerBorderWidth = "2px",
  colorPickerBorder = { width: "2px", color: "#000000" },
  colorPickerSize = "40px",

  // TOOGLE BTN (Tags and badges)
  toggleFontFamily = DEFAULT_FONT_FAMILY,
  toggleBgColor = "#FFFFFF00",
  toggleBorder = { width: "2px", color: "#EEEEEE" },
  toggleFontSize = "16px",
  toggleHeight = "50px",
  toggleWidth = "80px",
  toggleRadius = {
    TopLeft: "5px",
    TopRight: "5px",
    BottomLeft: "5px",
    BottomRight: "5px",
  },
  toggleSpacing = "10px",
  toggleTextColor = "#000000",
  toggleActiveBorder = { width: "2px", color: MAIN_COLOR },
  toggleActiveBgColor,
  toggleActiveTextColor = MAIN_COLOR,

  // SELECT
  selectFontFamily = DEFAULT_FONT_FAMILY,
  selectFontSize = "16px",
  selectHeight = "50px",
  selectWidth = "50%",
  selectBorder = { width: "1px", color: "#EEEEEE" },
  selectTextColor = "#000000",

  // ADD CART BUTTON
  btnFontFamily = DEFAULT_FONT_FAMILY,
  btnFontSize = "14px",
  btnBgColor = MAIN_COLOR,
  btnTextColor = "#FFFFFF",
  btnHeight = "50px",
  btnWidth = "250px",
  btnRadius = {
    TopLeft: "5px",
    TopRight: "5px",
    BottomLeft: "5px",
    BottomRight: "5px",
  },
  btnBorder,
  btnHoverBgColor,
  btnHoverTextColor = "#FFFFFF",

  inputFontFamily = DEFAULT_FONT_FAMILY,
  inputFontSize = "14px",
  inputContainerBgColor,
  inputContainerRadius = {
    TopLeft: "5px",
    TopRight: "5px",
    BottomLeft: "5px",
    BottomRight: "5px",
  },
  inputContainerBorder = { width: "2px", color: "#EEEEEE" },

  inputHeight = "50px",
  inputWidth = "250px",
  inputBorder = { width: "2px", color: "#FFFFFF" },
  inputTextColor = "#000000",

  inputBtnSize = "40px",
  inputBtnRadius = {
    TopLeft: "50%",
    TopRight: "50%",
    BottomLeft: "50%",
    BottomRight: "50%",
  },
  inputBtnBgColor = "#FFFFFF",
  inputBtnTextColor = "#000000",
  inputBtnHoverBgColor = "#000000",
  inputBtnHoverTextColor = "#FFFFFF",
}) => {
  return (
    <Stack
      id="XProductDetails_DetailsSide"
      spacing={detailsGap}
      alignItems="flex-start"
      sx={{
        fontFamily: "inherit",
        color: textColor,
        fontSize: textFontSize,
        position: "sticky",
        top: 0,

        "& p": { lineHeight: "1.2em" },

        // TITLE
        "& .XProductDetails_Title": {
          width: "100px",
          fontSize: titleFontSize,
          fontWeight: titleFontWeight,
          color: titleColor,
          letterSpacing: titleLetterSpacing,
        },

        // NAME
        "& .XProductDetails_Name": {
          fontFamily: nameFontFamily,
          fontStyle: nameFontStyle,
          fontSize: nameFontSize,
          fontWeight: nameFontWeight,
          color: nameColor,
        },

        // PRICE
        "& .XProductDetails_Price": {
          fontFamily: priceFontFamily,
          fontStyle: priceFontStyle,
          fontSize: priceFontSize,
          fontWeight: priceFontWeight,
          color: priceColor,
        },

        // DISCOUNT
        "& .XProductDetails_Discount": {
          fontFamily: discountFontFamily,
          fontStyle: discountFontStyle,
          fontSize: discountFontSize,
          fontWeight: discountFontWeight,
          color: discountColor,
          textDecoration: discountTextDecoration,
        },

        // REVIEWS
        "& .XProductDetails_Rating": {
          "& .MuiRating-icon": {
            fontSize: starIconSize,
          },
          "& .MuiRating-iconFilled": {
            color: starIconColor,
          },
          "& .MuiRating-iconHover": {
            color: starIconColor,
          },
        },

        // COLOR PICKER
        "& .XProductDetails_ColorPicker": {
          borderRadius: 0,

          "& .MuiToggleButton-root": {
            borderWidth: 0,
            p: "2px",
            bgcolor: "transparent",
            width: colorPickerSize,
            height: colorPickerSize,
            borderRadius: "50% !important",

            "& .XProductDetails_ColorPicker_Color": {
              borderRadius: "50%",
              border: "1px solid #ddd !important",
            },

            "&.Mui-selected": {
              border: generateBorder(colorPickerBorder),
            },

            "&:not(:first-of-type)": {
              ml: 1,
            },
          },
        },

        // TOGGLE BTN
        "& .MuiToggleButtonGroup-root.XProductDetails_ToggleBtn": {
          fontFamily: toggleFontFamily,
          fontSize: toggleFontSize,
          gap: toggleSpacing,
          flexWrap: "wrap",
          borderRadius: 0,

          "& .MuiToggleButton-root": {
            borderWidth: 0,
            p: "5px",
            bgcolor: toggleBgColor,
            borderRadius: generateRadius(toggleRadius),
            width: toggleWidth,
            height: toggleHeight,
            border: generateBorder(toggleBorder),
            color: toggleTextColor,

            "&.Mui-selected": {
              border: generateBorder(toggleActiveBorder),
              bgcolor: toggleActiveBgColor,
              color: toggleActiveTextColor,
            },
          },
        },

        // SELECT OPTIONS
        "& .XProductDetails_SelectOption": {
          fontFamily: selectFontFamily,
          fontSize: selectFontSize,
          height: selectHeight,
          width: selectWidth,
          minWidth: 150,
          border: generateBorder(selectBorder),
          color: selectTextColor,

          "&.Mui-focused": {
            "& fieldset": { border: 0 },
          },

          "& fieldset": { border: 0 },
        },

        // ADD CART BTN
        "& .XProductDetails_AddToCart": {
          fontFamily: btnFontFamily,
          bgcolor: btnBgColor,
          color: btnTextColor,
          fontSize: btnFontSize,
          height: btnHeight,
          width: btnWidth,
          borderRadius: generateRadius(btnRadius),
          border: generateBorder(btnBorder),
          textTransform: "none",

          "&:hover": {
            bgcolor: btnHoverBgColor ?? btnBgColor,
            color: btnHoverTextColor ?? btnTextColor,
          },
        },

        // ADD CART BTN
        // inputBtnHoverBgColor
        // inputBtnHoverTextColor
        "& .XProductDetails_QtyInput": {
          bgcolor: inputContainerBgColor,
          border: generateBorder(inputContainerBorder),
          borderRadius: generateRadius(inputContainerRadius),
          height: inputHeight,
          width: inputWidth,
          overflow: "hidden",

          "& .XProductDetails_QtyInput_Btn": {
            borderRadius: generateRadius(inputBtnRadius),
            boxShadow: "none",
            height: inputBtnSize,
            width: inputBtnSize,
            bgcolor: inputBtnBgColor,

            "&:not(.Mui-disabled)": {
              color: inputBtnTextColor,
            },

            "&:hover": {
              bgcolor: inputBtnHoverBgColor,
              color: inputBtnHoverTextColor,
            },
          },

          "& .XProductDetails_QtyInput_Field": {
            fontFamily: inputFontFamily,
            fontSize: inputFontSize,
            color: inputTextColor,
            flex: 1,

            "& input": {
              textAlign: "center",
            },

            "& fieldset": {
              border: generateBorder(inputBorder),
            },
          },
        },
      }}
    >
      {children}
    </Stack>
  );
};

export default DetailsWrapper;
